// On-device object tracker: template matching (SAD) on downscaled frames rendered with the canvas fit.
const TW = 192;

function gray(ctx, w, h) {
  const d = ctx.getImageData(0, 0, w, h).data, g = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) g[i] = d[i * 4] * 0.299 + d[i * 4 + 1] * 0.587 + d[i * 4 + 2] * 0.114;
  return g;
}

const seek = (v, s) => new Promise((res) => {
  const done = () => { v.removeEventListener("seeked", done); res(); };
  v.addEventListener("seeked", done);
  v.currentTime = Math.max(0, s);
  setTimeout(done, 1500);
});

// box: canvas-normalised {x, y, w, h}; times: [[timelineT, sourceT], ...]. Returns pts [[t, cx, cy], ...].
export async function trackRegion({ url, box, times, aspect, fitMode = "cover", onProgress, shouldStop }) {
  const v = document.createElement("video");
  v.crossOrigin = "anonymous"; v.muted = true; v.preload = "auto"; v.src = url;
  await new Promise((res, rej) => { v.onloadeddata = res; v.onerror = () => rej(new Error("Video could not be loaded for tracking")); });
  const W = TW, H = Math.max(16, Math.round(TW / (aspect || 16 / 9)));
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  const draw = () => {
    const vw = v.videoWidth, vh = v.videoHeight;
    const sc = fitMode === "contain" ? Math.min(W / vw, H / vh) : Math.max(W / vw, H / vh);
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
    ctx.drawImage(v, (W - vw * sc) / 2, (H - vh * sc) / 2, vw * sc, vh * sc);
    return gray(ctx, W, H);
  };
  const tw = Math.max(6, Math.round(box.w * W)), th = Math.max(6, Math.round(box.h * H));
  let px = Math.round(box.x * W), py = Math.round(box.y * H);
  await seek(v, times[0][1]);
  let g = draw();
  let tpl = new Float32Array(tw * th);
  for (let y = 0; y < th; y++) for (let x = 0; x < tw; x++) tpl[y * tw + x] = g[(py + y) * W + px + x] || 0;
  const pts = [[times[0][0], (px + tw / 2) / W, (py + th / 2) / H]];
  const R = Math.max(6, Math.round(Math.max(tw, th) * 0.6)), st = tw * th > 900 ? 2 : 1;
  for (let i = 1; i < times.length; i++) {
    if (shouldStop?.()) break;
    await seek(v, times[i][1]);
    g = draw();
    let best = Infinity, bx = px, by = py;
    for (let dy = -R; dy <= R; dy++) {
      const yy = py + dy; if (yy < 0 || yy + th > H) continue;
      for (let dx = -R; dx <= R; dx++) {
        const xx = px + dx; if (xx < 0 || xx + tw > W) continue;
        let sad = 0;
        for (let y = 0; y < th && sad < best; y += st) {
          const ro = (yy + y) * W + xx, to = y * tw;
          for (let x = 0; x < tw; x += st) sad += Math.abs(g[ro + x] - tpl[to + x]);
        }
        if (sad < best) { best = sad; bx = xx; by = yy; }
      }
    }
    px = bx; py = by;
    for (let y = 0; y < th; y++) for (let x = 0; x < tw; x++) tpl[y * tw + x] = tpl[y * tw + x] * 0.85 + (g[(py + y) * W + px + x] || 0) * 0.15;
    pts.push([times[i][0], (px + tw / 2) / W, (py + th / 2) / H]);
    onProgress?.(i / (times.length - 1));
  }
  v.removeAttribute("src"); v.load();
  return pts.map(([t, x, y]) => [Math.round(t * 1000) / 1000, Math.round(x * 10000) / 10000, Math.round(y * 10000) / 10000]);
}
